import { useEffect, useReducer, useRef } from 'react'
import { apiFetch, ApiError } from '@/api/client'
import { useAuthContext } from '@/auth/AuthProvider'

// ---------------------------------------------------------------------------
// Query state shape — same envelope every read hook returns.
// ---------------------------------------------------------------------------

export type QueryState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: T }
  | { status: 'error'; error: ApiError | Error }

type QueryAction<T> =
  | { type: 'FETCH' }
  | { type: 'SUCCESS'; data: T }
  | { type: 'ERROR'; error: ApiError | Error }

function queryReducer<T>(state: QueryState<T>, action: QueryAction<T>): QueryState<T> {
  switch (action.type) {
    case 'FETCH':
      return { status: 'loading' }
    case 'SUCCESS':
      return { status: 'success', data: action.data }
    case 'ERROR':
      return { status: 'error', error: action.error }
    default:
      return state
  }
}

// ---------------------------------------------------------------------------
// useApiQuery — generic auth-aware GET hook
// ---------------------------------------------------------------------------

/**
 * Issue an authenticated GET against the API and track its lifecycle.
 *
 * `path` is the full API path including query string. The hook re-fires
 * whenever `path` changes — list views drive search/filter changes by
 * rebuilding the path string.
 *
 * `refreshKey` is an optional secondary trigger: any change to it
 * forces a re-fetch even when the path is the same. Use it after a
 * mutation when the data on the server has changed but the read URL
 * hasn't (e.g. PATCH and then re-fetch the same initiative). Defaults
 * to 0 so existing callers don't need to opt in.
 *
 * The bearer token is acquired per call via `getToken()` (silent +
 * interactive fallback). Race condition: if two fetches are in flight
 * (e.g. user types fast), we discard results from anything other than
 * the most recent — there is no global request mutex; ordering is by
 * a per-mount sequence number.
 */
export function useApiQuery<T>(path: string, refreshKey: number = 0): QueryState<T> {
  const { getToken } = useAuthContext()

  const [state, dispatch] = useReducer(
    queryReducer as (s: QueryState<T>, a: QueryAction<T>) => QueryState<T>,
    { status: 'idle' },
  )

  // Capture latest getToken without re-firing the effect on every render.
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  // Per-effect-run sequence number; result of an older fetch is discarded
  // if a newer fetch has already started.
  const seqRef = useRef(0)

  useEffect(() => {
    const seq = ++seqRef.current
    let cancelled = false
    dispatch({ type: 'FETCH' })

    apiFetch<T>(path, () => getTokenRef.current())
      .then(data => {
        if (cancelled || seq !== seqRef.current) return
        dispatch({ type: 'SUCCESS', data })
      })
      .catch((err: unknown) => {
        if (cancelled || seq !== seqRef.current) return
        dispatch({
          type: 'ERROR',
          error: err instanceof Error ? err : new Error(String(err)),
        })
      })

    return () => {
      cancelled = true
    }
  }, [path, refreshKey])

  return state
}
